import React, { useState, useEffect } from 'react'
import Page from '../../components/Page'
import { Brain } from 'lucide-react'
import PeriodFilter from '../../components/PeriodFilter'
import AIInsightsDisplay from '../../pages/shared-components/AIInsightsDisplay'
import { useAnalyzeNFeVirtualByPeriodMutation, PeriodSearchRequest, MonthRange } from '../../services/api'
import { AIAssistantButton } from '../../components/AIAssistant'

const CACHE_KEY = 'nfe_ai_analysis_cache'
const CACHE_EXPIRY = 24 * 60 * 60 * 1000 // 24 horas

interface CachedData {
  data: any
  timestamp: number
  filters: { year: number; monthRanges: MonthRange[] }
}

const getCacheKey = (year: number, monthRanges: MonthRange[]) => {
  const sortedMonths = [...monthRanges].sort((a, b) => a.startMonth - b.startMonth)
  return `${year}_${sortedMonths.map(m => `${m.startMonth}-${m.endMonth}`).join('_')}`
}

const getCachedData = (cacheKey: string): CachedData | null => {
  try {
    const cached = localStorage.getItem(CACHE_KEY)
    if (!cached) return null

    const cacheMap = JSON.parse(cached)
    const cachedItem = cacheMap[cacheKey]

    if (!cachedItem) return null

    // Verificar se expirou
    const now = Date.now()
    if (now - cachedItem.timestamp > CACHE_EXPIRY) {
      // Remover item expirado
      delete cacheMap[cacheKey]
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheMap))
      return null
    }

    return cachedItem
  } catch (error) {
    // Cache read error - fail gracefully
    return null
  }
}

const setCachedData = (cacheKey: string, data: any, filters: { year: number; monthRanges: MonthRange[] }) => {
  try {
    const cached = localStorage.getItem(CACHE_KEY)
    const cacheMap = cached ? JSON.parse(cached) : {}

    cacheMap[cacheKey] = {
      data,
      timestamp: Date.now(),
      filters
    }

    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheMap))
  } catch (error) {
    // Cache write error - fail gracefully
  }
}

const NFesAnalysisPage: React.FC = () => {
  const [aiAnalysisData, setAiAnalysisData] = useState<any>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState<string | null>(null)
  const [hasSearched, setHasSearched] = useState(false)
  
  const [analyzeNFeVirtualByPeriod] = useAnalyzeNFeVirtualByPeriodMutation()

  // Carregar dados do cache ao montar o componente
  useEffect(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY)
      if (cached) {
        const cacheMap = JSON.parse(cached)
        // Pegar o primeiro item válido do cache
        const cacheEntries = Object.entries(cacheMap)
        if (cacheEntries.length > 0) {
          const [_, cachedItem] = cacheEntries[0] as [string, CachedData]

          // Verificar se expirou
          const now = Date.now()
          if (now - cachedItem.timestamp < CACHE_EXPIRY) {
            setAiAnalysisData(cachedItem.data)
            setHasSearched(true)
          }
        }
      }
    } catch (error) {
      // Fail silently on cache load error
    }
  }, [])

  const handleFilterStart = () => {
    setIsSearching(true)
    setSearchError(null)
    setAiAnalysisData(null)
    setHasSearched(true)
  }

  const handlePeriodFilter = async (year: number, monthRanges: MonthRange[]) => {
    try {
      // Verificar se existe no cache
      const cacheKey = getCacheKey(year, monthRanges)
      const cached = getCachedData(cacheKey)

      if (cached) {
        setAiAnalysisData(cached.data)
        setHasSearched(true)
        setIsSearching(false)
        return
      }

      // Se não existe no cache, buscar da API
      // Converter MonthRange[] para number[] (lista de meses individuais)
      const months: number[] = []
      monthRanges.forEach(range => {
        for (let month = range.startMonth; month <= range.endMonth; month++) {
          if (!months.includes(month)) {
            months.push(month)
          }
        }
      })

      const request: PeriodSearchRequest = {
        year,
        month_ranges: months,
        additional_filters: {}
      }

      const result = await analyzeNFeVirtualByPeriod(request).unwrap()

      if (result.success) {
        // Salvar no cache
        setCachedData(cacheKey, result.data, { year, monthRanges })

        setAiAnalysisData(result.data)
        setHasSearched(true)
      } else {
        setSearchError(result.message || 'Erro ao analisar NFes')
        setHasSearched(false)
      }
    } catch (error: any) {
      setSearchError(error.message || 'Erro interno do servidor')
      setHasSearched(false)
    } finally {
      setIsSearching(false)
    }
  }

  return (
    <Page
      title="📄 Insights Inteligentes de NFes"
      subtitle="Análise avançada com IA e filtros por período"
      onRefresh={() => window.location.reload()}
    >
      <div className="space-y-6">
        {/* Filtro de Período */}
        <PeriodFilter 
          onFilter={handlePeriodFilter}
          onFilterStart={handleFilterStart}
          isLoading={isSearching}
        />

        {/* AI Assistant Button - Show when data is loaded */}
        {hasSearched && aiAnalysisData && (() => {
          // Contexto normalizado para NFEs
          const screenContext = 'NFEs'

          return <AIAssistantButton screenContext={screenContext} />
        })()}

        {/* Exibição de Insights de IA */}
        <AIInsightsDisplay
          data={aiAnalysisData}
          type="nfe"
          isLoading={isSearching}
          error={searchError}
          hasSearched={hasSearched}
        />

        {/* Placeholder quando não há busca */}
        {!hasSearched && !isSearching && (
          <div className="text-center py-16">
            <div className="flex flex-col items-center space-y-4">
              <div className="p-6 rounded-full bg-green-100 dark:bg-green-900/30">
                <Brain className="h-12 w-12 text-green-500" />
              </div>
              <div>
                <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
                  🤖 Análise Inteligente de NFes
                </h3>
                <p className="text-sm max-w-md mx-auto" style={{ color: 'var(--text-secondary)' }}>
                  Selecione um período e clique em "Filtrar" para nossa IA analisar os dados de NFes da sua empresa e gerar insights estratégicos
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </Page>
  )
}

export default NFesAnalysisPage



